import { apiClient } from './client';

/**
 * 인사평가 — 회차 운영 API (인사, migration 134 · 시안 A21).
 *
 * 서버: `apps/chat-server/src/modules/hr/evaluations.routes.ts`.
 *
 * ★★ **진행률 · 분포 · 배분 초과는 전부 서버가 센다.** 화면은 그 값을 그리기만 한다 —
 *    「미제출이 몇 명인가」 의 정의가 두 곳에 있으면 지표와 표가 서로 다른 말을 한다.
 * ★ **옛 서버에는 이 라우트가 없다**(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export const EVAL_STAGES = ['SELF', 'FIRST', 'SECOND'] as const;
export type EvalStage = (typeof EVAL_STAGES)[number];
export const EVAL_STAGE_LABEL: Record<string, string> = {
  SELF: '자기평가', FIRST: '1차 평가', SECOND: '2차 평가',
};

export const EVAL_STATUSES = ['PLANNED', 'ACTIVE', 'CLOSED'] as const;
export type EvalStatus = (typeof EVAL_STATUSES)[number];
export const EVAL_STATUS_LABEL: Record<string, string> = {
  PLANNED: '예정', ACTIVE: '진행', CLOSED: '완료',
};

export const EVAL_GRADES = ['S', 'A', 'B', 'C', 'D'] as const;
export type EvalGrade = (typeof EVAL_GRADES)[number];

export interface RoundDto {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  statusLabel: string;
  /** 재직 N개월 이상만 대상. 0 이면 전원. */
  eligibleMonths: number;
  useSelf: boolean;
  useFirst: boolean;
  useSecond: boolean;
  /** 이 회차가 실제로 쓰는 단계 — 순서대로. */
  stages: EvalStage[];
  /** 등급→퍼센트. **비면 «제한 없음»** 이다(0% 가 아니다). */
  quota: Record<string, number>;
  confirmedAt: string | null;
  note: string | null;
  createdAt: string;
  targetCount?: number;
}

export interface StageProgressDto {
  stage: EvalStage;
  label: string;
  done: number;
  rate: number;
}

export interface ProgressDto {
  target: number;
  stages: StageProgressDto[];
  /** 쓰는 단계를 **전부** 마치지 못한 사람 = 독촉 대상. 표의 «독촉» 행 수와 같다. */
  pending: number;
  graded: number;
}

export interface QuotaBreachDto {
  grade: string;
  /** 배분이 허용하는 인원 — **대상자 전체** 기준의 상한. */
  allowed: number;
  actual: number;
  over: number;
}

export interface EvaluationRowDto {
  id: string;
  userId: string;
  name: string | null;
  employeeNumber: string | null;
  /** ★ **확정 시점의 소속**이다 — 지금 소속이 아니다. */
  teamName: string | null;
  self: boolean;
  first: boolean;
  second: boolean;
  firstScore: number | null;
  secondScore: number | null;
  /** 2차가 있으면 2차, 없으면 1차. 서버가 고른다. */
  score: number | null;
  grade: string | null;
  /** 다음에 해야 할 단계 — null 이면 다 마쳤다. */
  nextStage: EvalStage | null;
  complete: boolean;
  note: string | null;
}

export interface RoundDetailDto {
  round: RoundDto;
  progress: ProgressDto;
  distribution: Record<string, number>;
  /** 지금 확정하면 막힐 등급 — 비어 있으면 확정할 수 있다. */
  breaches: QuotaBreachDto[];
  rows: EvaluationRowDto[];
}

export async function fetchRounds(): Promise<{ rounds: RoundDto[] }> {
  const { data } = await apiClient.get<{ rounds: RoundDto[] }>('/hr/evaluations/rounds');
  return data;
}

export async function fetchRound(id: string): Promise<RoundDetailDto> {
  const { data } = await apiClient.get<RoundDetailDto>(`/hr/evaluations/rounds/${id}`);
  return data;
}

export interface RoundInput {
  name: string;
  periodStart: string;
  periodEnd: string;
  status?: EvalStatus;
  eligibleMonths?: number;
  useSelf?: boolean;
  useFirst?: boolean;
  useSecond?: boolean;
  quota?: Record<string, number>;
  note?: string | null;
}

export async function createRound(input: RoundInput): Promise<RoundDto> {
  const { data } = await apiClient.post<{ round: RoundDto }>('/hr/evaluations/rounds', input);
  return data.round;
}

export async function updateRound(id: string, patch: Partial<RoundInput>): Promise<RoundDto> {
  const { data } = await apiClient.patch<{ round: RoundDto }>(`/hr/evaluations/rounds/${id}`, patch);
  return data.round;
}

export async function deleteRound(id: string): Promise<void> {
  await apiClient.delete(`/hr/evaluations/rounds/${id}`);
}

export interface ConfirmTargetsDto {
  added: number;
  total: number;
  /** 입사일이 없어 명단에서 빠진 사람 — 화면이 이 수를 말한다. */
  skippedNoHireDate: number;
}

/** 대상 확정 — **여러 번 눌러도 안전하다**(새로 자격을 얻은 사람만 는다). */
export async function confirmTargets(roundId: string): Promise<ConfirmTargetsDto> {
  const { data } = await apiClient.post<ConfirmTargetsDto>(`/hr/evaluations/rounds/${roundId}/targets`);
  return data;
}

/** 등급 확정 — 배분을 넘으면 409 이고 `breaches` 가 무엇이 몇 명 넘었는지 말한다. */
export async function confirmGrades(roundId: string): Promise<RoundDto> {
  const { data } = await apiClient.post<{ round: RoundDto }>(`/hr/evaluations/rounds/${roundId}/confirm`);
  return data.round;
}

export interface EvaluationPatch {
  self?: boolean;
  first?: boolean;
  second?: boolean;
  firstScore?: number | null;
  secondScore?: number | null;
  grade?: EvalGrade | null;
  note?: string | null;
}

export async function updateEvaluation(id: string, patch: EvaluationPatch): Promise<EvaluationRowDto> {
  const { data } = await apiClient.patch<{ evaluation: EvaluationRowDto }>(`/hr/evaluations/${id}`, patch);
  return data.evaluation;
}

export interface UserEvaluationDto {
  roundId: string;
  roundName: string;
  /** 확정 전 회차는 등급·점수가 **null 로 온다** — 바뀔 수 있는 값이라 서버가 말하지 않는다. */
  confirmed: boolean;
  score: number | null;
  grade: string | null;
  teamName: string | null;
}

/** 한 사람의 지난 평가 — 담당자와 **본인**이 같은 라우트를 쓴다(시안 B2 가 쓸 자리). */
export async function fetchUserEvaluations(userId: string): Promise<{ rows: UserEvaluationDto[] }> {
  const { data } = await apiClient.get<{ rows: UserEvaluationDto[] }>(`/hr/evaluations/user/${userId}`);
  return data;
}
